import { useMutation } from '@tanstack/react-query'

import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { useFindProfile } from '@hooks/profile'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'
import { api } from '@services/apiClient'
import { queryClient } from '@services/queryClient'

interface IPutTaskParams {
  ids: number[]
  changes: Partial<IGenericTask>
}

interface IPutTaskComplete extends IPutTaskParams {
  urlSuffix: 'update' | 'planningTaskStatus'
}

export function useBulkEditTasks() {
  const { data: user } = useFindProfile()
  const { selectedDate } = useSharedDates()
  const { getWeekRange } = useDate()
  const { weekStart } = getWeekRange(selectedDate)

  const siteId = user && user.selectedSite.id

  async function putTasks({ ids, changes, urlSuffix }: IPutTaskComplete) {
    const updates = { ids, changes }
    const url = `/sites/${siteId}/tasks/${urlSuffix}`
    const { data } = await api.put<IGenericTask[]>(url, updates)

    return data
  }

  return useMutation({
    mutationFn: putTasks,
    onMutate: handleOptimisticBulkEditTasks,
    onError: (_err, _newTask, context) => {
      if (!context) return

      const { previousTasks, tasksKey } = context
      queryClient.setQueryData(tasksKey, previousTasks)
    },
    onSettled: (_tasks, _err, _variables, context) =>
      queryClient.invalidateQueries({
        queryKey: context?.tasksKey,
      }),
  })

  async function handleOptimisticBulkEditTasks({
    changes,
    ids,
  }: IPutTaskParams) {
    await queryClient.cancelQueries({ queryKey: ['TASKS'] })
    const [tasksQuery] = queryClient.getQueriesData<IGenericTask[]>({
      queryKey: ['TASKS', weekStart.getTime()],
      type: 'active',
    })

    const [tasksKey, tasksResult] = tasksQuery
    const previousTasks = tasksResult

    const updatedTasks = tasksResult?.map((task) => {
      if (ids.includes(task.id)) {
        return {
          ...task,
          ...changes,
        }
      }
      return task
    })

    queryClient.setQueryData(tasksKey, () => updatedTasks)

    return {
      tasksKey,
      previousTasks,
      updatedTasks,
    }
  }
}
